/**
 * Cost centres — the client's read-only half of a system-managed dimension.
 *
 * docs/CLINIC_COST_CENTER_AUTO_MAPPING_DESIGN.md.
 *
 * Nobody defines, picks or edits a cost centre. Every expense declares where it
 * arose — an origin domain and an origin event — and the accounting service derives
 * the centre from that pair plus the nature-of-cost category, at write time, on a
 * row that is never rewritten afterwards.
 *
 * This module exists for two jobs, and neither of them decides anything:
 *
 *   1. LABELS — turning the codes that come back on an expense, a report row or a
 *      P&L line into «تأمین و خرید». Exactly what `categoryLabel` does for
 *      categories; the centre is one more closed vocabulary, not a lookup table.
 *   2. PREVIEW — showing the person filling in a form which centre their row will
 *      land in, as they change the category, BEFORE they save. Without it the
 *      automatic mapping would be invisible and would read as data going missing.
 *
 * `costCenterMap.json` is byte-identical to the accounting service's own
 * `app/domain/cost_center_map.json` (`make cost-center-map-check`), because a drifted
 * copy would promise one centre on the form and file another in the ledger — and
 * nothing would fail.
 *
 * The SERVICE decides. A preview that disagrees with the row that comes back is a
 * bug in this file, never a reason to send a centre: there is no field to send one in.
 */
import MAP from '../constants/costCenterMap.json';

/** The origin a row a PERSON types carries. Its event says nothing beyond «somebody
 *  entered this», so for this domain the category is the whole rule. */
export const MANUAL_ORIGIN_DOMAIN = 'expenses';

/** Every centre code, in the order the map declares them. */
export const COST_CENTER_KEYS = Object.keys(MAP.cost_centers);

const RULES = MAP.rules.map((rule) => {
  const [domain, ...rest] = String(rule.origin).split('.');
  const event = rest.join('.');
  const wildcard = event.endsWith('*');
  return {
    domain,
    prefix: wildcard ? event.slice(0, -1) : event,
    wildcard,
    category: rule.category ?? null,
    costCenter: rule.cost_center,
  };
});

/**
 * The label for a centre code, from the i18n bundle with the map's own Persian as
 * the fallback — so a centre added to the map before its translation key still
 * renders as a name rather than as `hr`.
 */
export function costCenterLabel(key, t) {
  if (!key) return '';
  const known = MAP.cost_centers[key];
  // Not one of the six: shown as sent. It used to ask the translator for it anyway and compare the
  // answer with the full key to detect a miss — but a miss comes back as its last segment
  // HUMANISED, so `personnel` drew «Personnel» (PM-0740). The caller decides what else to call it.
  if (!known) return key;
  const path = `operations.costCenters.${key}`;
  // The map's own Persian is the fallback when the bundle has no word yet.
  const translated = t ? t(path, known.fa) : null;
  return translated && translated !== path ? translated : known.fa;
}

/** Options for a centre filter — the whole vocabulary, never a fetched list. */
export const costCenterOptions = (t) =>
  COST_CENTER_KEYS.map((key) => ({ value: key, label: costCenterLabel(key, t) }));

/**
 * Which centre a row from this origin and category will be filed under, or null
 * when nothing matches.
 *
 * The same three steps the service's resolver takes, in the same order: an exact
 * origin beats a wildcard one, and among equally specific origins a rule that also
 * names a category beats one that does not. Returns null rather than a default —
 * the service refuses such a row, and a preview that invented «اداری» here would be
 * promising something that is about to fail.
 */
export function resolveCostCenter(domain, event, category) {
  let best = null;
  let bestScore = null;
  for (const rule of RULES) {
    if (rule.domain !== domain) continue;
    if (rule.wildcard ? !String(event).startsWith(rule.prefix) : event !== rule.prefix) continue;
    if (rule.category !== null && rule.category !== category) continue;
    const score = [rule.wildcard ? 0 : 1, rule.prefix.length, rule.category !== null ? 1 : 0];
    if (!bestScore || score > bestScore) {
      best = rule.costCenter;
      bestScore = score;
    }
  }
  return best;
}

/**
 * The centre a row a person is typing right now will land in — the preview.
 *
 * Takes only the category, because that is the only thing the form lets them
 * change: the origin of anything typed on the expense screen is always
 * expenses/manual (or expenses/recurring for a template, which resolves the same
 * way).
 */
export const previewCostCenterForCategory = (category) =>
  resolveCostCenter(MANUAL_ORIGIN_DOMAIN, 'manual', category);

/**
 * How a row's own origin reads in Persian — «خرید کالای انبار», «ثبت دستی».
 *
 * Shown next to the derived centre on a row's detail, so «چرا این ردیف اینجاست؟»
 * has an answer on the screen instead of in a design document.
 */
export function originLabel(domain, event, t) {
  if (!domain || !event) return '';
  const key = `operations.origins.${domain}.${event}`;
  const translated = t ? t(key) : null;
  if (translated && translated !== key) return translated;
  // A bill's event is «bill.<kind>»; fall back to the domain's own label rather
  // than printing a dotted machine key at a person.
  const domainKey = `operations.origins.${domain}._`;
  const fallback = t ? t(domainKey) : null;
  return fallback && fallback !== domainKey ? fallback : `${domain}.${event}`;
}
